import {
	getGetAccountQueryKey,
	useUpdateAccount,
} from '@cocoonspace/sdk-js/domains/account/account'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { checkVAT, countries } from 'jsvat'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'sonner'
import { z } from 'zod'
import { Button } from '~/components/ui/button'
import { CountrySelect } from '~/components/ui/country-select'
import { Form } from '~/components/ui/form'
import { FormControlledInput } from '~/components/ui/form-controlled-input'
import { Input } from '~/components/ui/input'
import { useTracking } from '~/providers/tracking-provider'
import { useUserContext } from '~/providers/user-provider'

export type AddressFactuFormValues = z.infer<typeof schema>

const nonemptyMessage = 'errors:forms.required'

const schema = z.object({
	company: z.string().optional(),
	email: z.string().optional(),
	address: z.string().min(1, { message: nonemptyMessage }),
	postal_code: z.string().min(1, { message: nonemptyMessage }),
	city: z.string().min(1, { message: nonemptyMessage }),
	country_alpha2: z.string().min(1, { message: nonemptyMessage }),
	vat_number: z
		.string()
		.optional()
		.refine(
			(val) => {
				if (!val) return true

				return checkVAT(val, countries).isValid
			},
			{ message: 'errors:forms.invalidFormat' },
		),
})

export const AddressFactuForm = ({
	onSuccess,
}: { onSuccess?: VoidFunction }) => {
	const router = useRouter()
	const queryClient = useQueryClient()
	const { t, i18n } = useTranslation()
	const { track } = useTracking()
	const { accountQuery } = useUserContext()
	const updateAccount = useUpdateAccount()

	const refinedSchema = useMemo(() => {
		if (accountQuery.data?.business) {
			return schema.merge(
				z.object({
					email: z.string().min(1, { message: nonemptyMessage }).email(),
				}),
			)
		}

		return schema
	}, [accountQuery.data?.business])

	const form = useForm<AddressFactuFormValues>({
		resolver: zodResolver(refinedSchema),
		values: {
			company: accountQuery.data?.billing_address?.company || '',
			email: accountQuery.data?.billing_address?.email || '',
			address: accountQuery.data?.billing_address?.address || '',
			postal_code: accountQuery.data?.billing_address?.postal_code || '',
			city: accountQuery.data?.billing_address?.city || '',
			country_alpha2:
				accountQuery.data?.billing_address?.country_alpha2 || 'FR',
			vat_number: accountQuery.data?.billing_address?.vat_number || '',
		},
	})

	const onSubmit = (formValues: AddressFactuFormValues) => {
		const query = updateAccount
			.mutateAsync({ data: { billing_address: formValues } })
			.then(() => {
				track.billingAddressUpdate(router.route)

				queryClient.invalidateQueries({ queryKey: getGetAccountQueryKey() })
				onSuccess?.()
			})

		toast.promise(query, {
			loading: t('billingAddressForm:loadingMessage'),
			success: t('billingAddressForm:successMessage'),
			error: (err: any) =>
				t([
					`errors:api.${err.response?.data?.errors[0].code}`,
					'errors:generic',
				]),
		})
	}

	return (
		<form
			id='billing-informations-form'
			noValidate
			onSubmit={form.handleSubmit(onSubmit)}
		>
			<Form {...form}>
				<div className='grid grid-cols-2 gap-4'>
					<FormControlledInput
						name='company'
						label={t('billingAddressForm:company')}
						control={form.control}
						render={({ field }) => <Input {...field} />}
					/>

					<FormControlledInput
						name='vat_number'
						label={t('billingAddressForm:vat_intra')}
						control={form.control}
						render={({ field }) => <Input {...field} />}
					/>
				</div>

				<FormControlledInput
					name='email'
					label={t('billingAddressForm:emailFactu')}
					control={form.control}
					render={({ field }) => (
						<Input
							{...field}
							type='email'
						/>
					)}
				/>

				<div className='grid grid-cols-3 gap-4'>
					<div className='col-span-2'>
						<FormControlledInput
							name='address'
							label={t('billingAddressForm:address')}
							control={form.control}
							render={({ field }) => <Input {...field} />}
							required
						/>
					</div>

					<FormControlledInput
						name='postal_code'
						label={t('billingAddressForm:postal_code')}
						control={form.control}
						render={({ field }) => <Input {...field} />}
						required
					/>
				</div>

				<div className='grid grid-cols-2 gap-4'>
					<FormControlledInput
						name='city'
						label={t('billingAddressForm:city')}
						control={form.control}
						render={({ field }) => <Input {...field} />}
						required
					/>

					<FormControlledInput
						name='country_alpha2'
						label={t('billingAddressForm:country')}
						control={form.control}
						render={({ field }) => (
							<CountrySelect
								{...field}
								onValueChange={(value) => field.onChange(value)}
							/>
						)}
						required
					/>
				</div>

				<Button
					disabled={updateAccount.isLoading}
					type='submit'
					className='mt-4 w-full'
				>
					{t('billingAddressForm:validate')}
				</Button>
			</Form>
		</form>
	)
}
